'use client';

import type { PlanTodoItem } from '@terra/ui-design-system';
import {
  EvidenceCardWithHar,
  MarkdownPreview,
  Scroll,
  Skeleton,
  Text,
  TraceViewHeader,
} from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';
import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useCoPilotTraceSummary } from '../../hooks/api/useCoPilotTraceSummary';

type CoPilotTraceSummaryViewProps = {
  threadId: string;
  targetId?: string;
  plan?: PlanTodoItem[];
  status?: 'running' | 'completed' | 'error' | 'aborted';
  enabled?: boolean;
};

export function CoPilotTraceSummaryView({
  threadId,
  targetId,
  plan,
  status = 'completed',
  enabled = true,
}: CoPilotTraceSummaryViewProps) {
  const t = useTranslations('chat.copilot.executions');
  const tExec = useTranslations('designSystem.executions');
  const previousStatusRef = useRef(status);

  const {
    summary: summaryData,
    isLoading,
    error,
    refetch,
  } = useCoPilotTraceSummary({ threadId, targetId, enabled });

  useEffect(() => {
    if (previousStatusRef.current === 'running' && status === 'completed') {
      void refetch();
    }

    previousStatusRef.current = status;
  }, [refetch, status]);

  const hasSummaryText = Boolean(summaryData?.summary?.trim());
  const hasEvidences = (summaryData?.evidences?.length ?? 0) > 0;
  const isEmpty = !isLoading && !error && !hasSummaryText && !hasEvidences;

  return (
    <Box h="full" w="full" overflow="hidden" p={4}>
      <VStack gap={0} h="full" alignItems="stretch">
        <Box flexShrink={0} pb={2}>
          <TraceViewHeader plan={plan} status={status} variant="summary" />
        </Box>

        <Box flex={1} overflow="hidden" pt={2}>
          {isLoading ? (
            <VStack gap={3} alignItems="stretch" paddingInline={2}>
              <Skeleton h="80px" w="full" borderRadius="md" />
              <Skeleton h="120px" w="full" borderRadius="md" />
            </VStack>
          ) : error ? (
            <Box paddingInline={2}>
              <Text fontSize="sm" color="fg.error">
                {t('summaryLoadError')}
              </Text>
            </Box>
          ) : isEmpty ? (
            <Box paddingInline={2}>
              <Text fontSize="sm" color="fg.muted">
                {t('summaryEmpty')}
              </Text>
            </Box>
          ) : (
            <Scroll orientation="vertical" showScrollbar="hover">
              <VStack gap={4} pb={4} alignItems="stretch" paddingInline={2}>
                {hasSummaryText ? (
                  <Box>
                    <Text fontSize="xs" color="fg.muted" mb={1}>
                      {tExec('summaryView.conclusion')}
                    </Text>
                    <Box color="fg.default">
                      <MarkdownPreview
                        source={summaryData?.summary ?? ''}
                        fontSize={12}
                        compact
                      />
                    </Box>
                  </Box>
                ) : null}

                {hasEvidences ? (
                  <Box>
                    <Text fontSize="xs" color="fg.muted" mb={2}>
                      {tExec('evidences')}
                    </Text>
                    <VStack gap={2} alignItems="stretch">
                      {summaryData?.evidences.map((evidence, index) => (
                        <EvidenceCardWithHar
                          key={evidence.toolExecutionHarId}
                          index={index}
                          toolExecutionHarId={evidence.toolExecutionHarId}
                          explanation={evidence.explanation}
                          defaultExpanded={index === 0}
                        />
                      ))}
                    </VStack>
                  </Box>
                ) : null}
              </VStack>
            </Scroll>
          )}
        </Box>
      </VStack>
    </Box>
  );
}
